import { useEffect, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useAuthStore } from 'lib/stores/authStore';
import { useTickerQuotesStore, type LifecycleStatus, type QuoteData } from 'lib/stores/tickerQuotesStore';

export interface LiveMarketData {
    quote: QuoteData | undefined;
    status: LifecycleStatus | undefined;
}

const EMPTY_RESULT: Record<string, LiveMarketData> = {};
const EMPTY_FLAT: Array<QuoteData | LifecycleStatus | undefined> = [];

/**
 * Read-side hook that exposes live ticker quotes + lifecycle status from
 * `tickerQuotesStore` for a set of tickers, and ref-counted-subscribes to each
 * via the store's watcher.
 *
 * Internally memoizes by content key (sorted-joined) so callers can pass a
 * fresh array every render without thrashing the watch/unwatch effect or the
 * zustand selector. The final result map is keyed by ticker, so input order
 * is not observable.
 *
 * Gated on `isLoggedIn`, not `isLoggedInMember`: applicants and `fcm_member`
 * accounts are logged-in but not in `MEMBER_ACCESS_LEVELS`, and grid surfaces
 * (`MarketGridClient`, etc.) disable fallback polling for any logged-in user.
 * Tile data would stale out for the entire session if we gated on member here.
 *
 * Logged-out callers skip both the subscription and the store read so
 * Storybook / public renders stay on their static fixture data, and stale
 * member-session data still cached in the store after a sign-out-without-refresh
 * can't leak into anonymous views.
 */
export function useLiveMarketQuotes(tickers: readonly string[]): Record<string, LiveMarketData> {
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

    // Content key — stable when the SET of tickers hasn't changed, regardless
    // of caller-side reference identity or input order. Memoized on the array's
    // identity so callers that hold a stable reference (`useLiveEventMarkets`
    // with a big event) skip the O(n log n) sort+join on renders they didn't
    // cause; fresh-array callers just recompute to the same key, exactly as
    // before.
    const tickersKey = useMemo(() => [...tickers].sort().join('|'), [tickers]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableTickers = useMemo(() => tickers, [tickersKey]);

    // Flat tuple of [quote, status, quote, status, ...] keeps `useShallow`
    // doing item-by-item reference comparison. The store keeps the outer
    // `quotes` / `statuses` records stable and replaces only changed ticker
    // entries, so unrelated WS ticks do not re-render this consumer.
    const flat = useTickerQuotesStore(
        useShallow((s) => {
            // Short-circuit logged-out reads so the selector skips the O(2n)
            // iteration on every store update for tiles that won't surface
            // live data anyway.
            if (!isLoggedIn) return EMPTY_FLAT;
            const out: Array<QuoteData | LifecycleStatus | undefined> = [];
            for (const ticker of stableTickers) {
                out.push(s.quotes[ticker], s.statuses[ticker]);
            }
            return out;
        }),
    );
    const { watch, unwatch } = useTickerQuotesStore((s) => s.actions);

    useEffect(
        function manageWsSubscriptions() {
            if (!isLoggedIn || stableTickers.length === 0) return;
            for (const ticker of stableTickers) watch(ticker);
            return () => {
                for (const ticker of stableTickers) unwatch(ticker);
            };
        },
        [isLoggedIn, stableTickers, watch, unwatch],
    );

    return useMemo(() => {
        if (!isLoggedIn || stableTickers.length === 0) return EMPTY_RESULT;
        const result: Record<string, LiveMarketData> = {};
        for (let i = 0; i < stableTickers.length; i++) {
            result[stableTickers[i]] = {
                quote: flat[i * 2] as QuoteData | undefined,
                status: flat[i * 2 + 1] as LifecycleStatus | undefined,
            };
        }
        return result;
    }, [isLoggedIn, stableTickers, flat]);
}

/**
 * Whether a market card's ticker subscription has delivered at least one quote snapshot.
 *
 * This deliberately observes the cache without acquiring another watch. The card that renders the
 * market owns the subscription; polling fallbacks can use this signal to stay active while that
 * subscription is pending, deferred, or otherwise has not produced usable data yet.
 */
export function useHasLiveMarketQuote(ticker: string): boolean {
    return useTickerQuotesStore((state) => !!ticker && state.quotes[ticker] !== undefined);
}

/**
 * Whether ANY of `tickers` has delivered a quote snapshot — the whole-event form of
 * `useHasLiveMarketQuote`, for surfaces whose fallback poll covers every market at once
 * (`ForecastEventWidget`). Same non-acquiring cache read: the rendering surface owns the
 * subscriptions; this only reports on them. O(n) per store write, but the boolean output means
 * subscribers re-render only when it flips.
 */
export function useHasAnyLiveMarketQuote(tickers: readonly string[]): boolean {
    return useTickerQuotesStore((state) => tickers.some((ticker) => state.quotes[ticker] !== undefined));
}
